import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Typography, styled } from '@mui/material';
import React, { ChangeEvent, useRef } from 'react';
import DeleteIcon from './icons/DeleteIcon';

interface CustomFileUploadProps {
  label?: string;
  accept?: string;
  required?: boolean;
  onChange: (file: File | null) => void;
  width?: string;
  error?: string;
  value?: File | null | string;
  disabled?: boolean;
}

const UploadBox = styled(Box)({
  border: '1px solid #D0D5DD',
  borderRadius: '8px',
  padding: '8px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  height: '31px',
  backgroundColor: '#F1F3FF',
  '&:hover': {
    borderColor: '#6E3FF3',
  },
  '&.error': {
    borderColor: '#F04438',
  },
  '&.disabled': {
    backgroundColor: '#00000014',
    cursor: 'not-allowed',
    borderColor: '#00000014',
  },
});

const HiddenInput = styled('input')({
  display: 'none',
});

const Label = styled(Typography)({
  color: '#666666',
  fontSize: '14px',
  marginBottom: '6px',
  display: 'flex',
  alignItems: 'center',
  '& .required': {
    color: '#F04438',
    marginLeft: '4px',
  },
});

const UploadText = styled(Typography)({
  color: '#6E3FF3',
  fontSize: '14px',
  flex: 1,
});

const ErrorText = styled(Typography)({
  color: '#F04438',
  fontSize: '14px',
  marginTop: '6px',
});

const PreviewBox = styled(Box)<{ width?: string }>(({ width }) => ({
  width: width || '174px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  padding: '8px 14px',
  backgroundColor: '#F1F3FF',
  borderRadius: '8px',
  height: '31px',
}));

const FileName = styled(Typography)({
  color: '#101828',
  fontSize: '12px',
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Delete = styled('div')({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.8,
  },
  '&.disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const FileUploadField: React.FC<CustomFileUploadProps> = ({
  label,
  accept = '.pdf,.docx,.doc',
  required = false,
  onChange,
  width,
  error,
  value,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = event.target.files?.[0] || null;
    onChange(file);
  };

  const handleDelete = () => {
    if (disabled) return;
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <Box>
      {label && (
        <Label className="input-field-label">
          {label}
          {required && <span>*</span>}
        </Label>
      )}
      {value && (
        <PreviewBox width={width}>
          <FileName>{(value instanceof File ? value.name : value)}</FileName>
          {!disabled && <Delete onClick={handleDelete} >
            <DeleteIcon />
          </Delete>}
        </PreviewBox>
      )}
      <UploadBox
        onClick={handleClick}
        className={`${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        width={width}
      >
        <UploadText>Upload</UploadText>
        <FileUploadOutlinedIcon sx={{ color: '#6E3FF3', fontSize: 20 }} />
      </UploadBox>
      <HiddenInput
        type="file"
        ref={inputRef}
        accept={accept}
        onChange={handleChange}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </Box>
  );
};

export default FileUploadField; 